import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Grid,
  IconButton,
  Divider,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowForward as ArrowForwardIcon
} from '@mui/icons-material';
import workflowService from '../../services/workflow';
import AppService from '../../services/api';
import { API_PATHS } from '../../utils/constants';

// Component for configuring service dependencies
const ServiceDependencyConfig = ({ 
  serviceIndex, 
  availableServices, 
  onDependencyAdd, 
  onDependencyRemove, 
  dependencies = [] 
}) => {
  const [selectedService, setSelectedService] = useState('');
  const [dependencyType, setDependencyType] = useState('FINISH_TO_START');
  
  const handleServiceChange = (event) => {
    setSelectedService(event.target.value);
  };
  
  const handleDependencyTypeChange = (event) => {
    setDependencyType(event.target.value);
  };
  
  const handleAddDependency = () => {
    if (selectedService) {
      onDependencyAdd(serviceIndex, selectedService, dependencyType);
      setSelectedService('');
    }
  };
  
  // Filter out the current service and services that are already dependencies
  const filteredServices = availableServices.filter((service, index) => 
    index !== serviceIndex && 
    !dependencies.some(dep => dep.stepId === `step-${index + 1}`)
  );
  
  return (
    <Box mt={2} mb={2}>
      <Typography variant="subtitle2" gutterBottom>Dependencies</Typography>
      
      {dependencies.map((dep, depIndex) => (
        <Box 
          key={depIndex} 
          display="flex" 
          alignItems="center" 
          mb={1} 
          p={1} 
          border="1px solid #e0e0e0" 
          borderRadius={1}
        >
          <Typography variant="body2">
            {availableServices[parseInt(dep.stepId.split('-')[1]) - 1]?.serviceName || 'Service'}
          </Typography>
          <Chip 
            size="small" 
            label={dep.type} 
            color="primary" 
            variant="outlined" 
            sx={{ mx: 1 }} 
          />
          <Box flexGrow={1} />
          <IconButton 
            size="small" 
            color="error" 
            onClick={() => onDependencyRemove(serviceIndex, depIndex)}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      ))}
      
      {filteredServices.length > 0 && (
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Depends on</InputLabel>
              <Select
                value={selectedService}
                onChange={handleServiceChange}
                label="Depends on"
              >
                {filteredServices.map((service, index) => (
                  <MenuItem key={index} value={`step-${index + 1}`}>
                    {service.serviceName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={5}>
            <FormControl size="small" fullWidth>
              <InputLabel>Dependency Type</InputLabel>
              <Select
                value={dependencyType}
                onChange={handleDependencyTypeChange}
                label="Dependency Type"
              >
                <MenuItem value="FINISH_TO_START">Finish to Start</MenuItem>
                <MenuItem value="START_TO_START">Start to Start</MenuItem>
                <MenuItem value="FINISH_TO_FINISH">Finish to Finish</MenuItem>
                <MenuItem value="START_TO_FINISH">Start to Finish</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={2}>
            <Button 
              variant="outlined" 
              color="primary" 
              size="small" 
              onClick={handleAddDependency}
              disabled={!selectedService}
              startIcon={<AddIcon />}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      )}
      
      {filteredServices.length === 0 && dependencies.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No available services for dependencies
        </Typography>
      )}
    </Box>
  );
};

// Component for configuring a single service
const ServiceConfigItem = ({ 
  serviceIndex, 
  serviceConfig, 
  availableServiceGroups = [], 
  availableServices = [], 
  onChange, 
  onRemove, 
  availableServiceConfigs, 
  onDependencyAdd, 
  onDependencyRemove,
  dependencies = []
}) => {
  const [selectedServiceGroup, setSelectedServiceGroup] = useState(serviceConfig.serviceGroupId || '');
  const [groupServices, setGroupServices] = useState([]);
  
  // Update available services when service group changes
  useEffect(() => {
    if (selectedServiceGroup) {
      const groupServices = availableServices.filter(
        service => service.serviceGroup && service.serviceGroup.id === selectedServiceGroup
      );
      setGroupServices(groupServices);
      
      // If current service is not in this group, reset it
      if (serviceConfig.serviceId && 
          !groupServices.some(service => service.id === serviceConfig.serviceId)) {
        onChange(serviceIndex, { 
          ...serviceConfig, 
          serviceId: '',
          serviceName: ''
        });
      }
    } else {
      setGroupServices([]);
    }
  }, [selectedServiceGroup, availableServices]);
  
  const handleServiceGroupChange = (event) => {
    const groupId = event.target.value;
    setSelectedServiceGroup(groupId);
    
    // Find the selected group name
    const groupName = availableServiceGroups.find(group => group.id === groupId)?.name || '';
    
    onChange(serviceIndex, { 
      ...serviceConfig, 
      serviceGroupId: groupId,
      serviceGroupName: groupName,
      serviceId: '',
      serviceName: '' 
    });
  };
  
  const handleServiceChange = (event) => {
    const serviceId = event.target.value;
    
    // Find the selected service name
    const serviceName = availableServices.find(service => service.id === serviceId)?.name || '';
    
    onChange(serviceIndex, { 
      ...serviceConfig, 
      serviceId: serviceId,
      serviceName: serviceName
    });
  };
  
  const handleExecutionModeChange = (event) => {
    onChange(serviceIndex, { 
      ...serviceConfig, 
      executionMode: event.target.value 
    });
  };
  
  const handleRequiredChange = (event) => {
    onChange(serviceIndex, { 
      ...serviceConfig, 
      required: event.target.checked 
    });
  };
  
  return (
    <Paper sx={{ p: 2, mb: 2, position: 'relative' }}>
      <IconButton 
        size="small" 
        color="error" 
        onClick={() => onRemove(serviceIndex)}
        sx={{ position: 'absolute', top: 8, right: 8 }}
      >
        <DeleteIcon />
      </IconButton>
      
      <Typography variant="subtitle1" gutterBottom>
        Service {serviceIndex + 1}
      </Typography>
      
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Service Group</InputLabel>
            <Select
              value={selectedServiceGroup}
              onChange={handleServiceGroupChange}
              label="Service Group"
            >
              <MenuItem value="">
                <em>Select a service group</em>
              </MenuItem>
              {availableServiceGroups.map((group) => (
                <MenuItem key={group.id} value={group.id}>
                  {group.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth disabled={!selectedServiceGroup}>
            <InputLabel>Service</InputLabel>
            <Select
              value={serviceConfig.serviceId}
              onChange={handleServiceChange}
              label="Service"
            >
              <MenuItem value="">
                <em>Select a service</em>
              </MenuItem>
              {groupServices.map((service) => (
                <MenuItem key={service.id} value={service.id}>
                  {service.name}
                </MenuItem>
              ))}
            </Select>
            {!selectedServiceGroup && (
              <FormHelperText>Select a service group first</FormHelperText>
            )}
          </FormControl>
        </Grid>
        
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Execution Mode</InputLabel>
            <Select
              value={serviceConfig.executionMode}
              onChange={handleExecutionModeChange}
              label="Execution Mode"
            >
              <MenuItem value="SEQUENTIAL">Sequential</MenuItem>
              <MenuItem value="PARALLEL">Parallel</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        
        <Grid item xs={12}>
          <Grid container alignItems="center">
            <Grid item>
              <FormControl component="fieldset">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <input 
                    type="checkbox" 
                    id={`required-${serviceIndex}`}
                    checked={serviceConfig.required}
                    onChange={handleRequiredChange}
                  />
                  <label htmlFor={`required-${serviceIndex}`}>Required</label>
                </div>
              </FormControl>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      
      {serviceConfig.serviceId && (
        <ServiceDependencyConfig 
          serviceIndex={serviceIndex}
          availableServices={availableServiceConfigs}
          onDependencyAdd={onDependencyAdd}
          onDependencyRemove={onDependencyRemove}
          dependencies={dependencies}
        />
      )}
    </Paper>
  );
};

// Main component for multi-service workflow configuration
const MultiServiceWorkflowBuilder = ({ token, customerUid, onWorkflowGenerated }) => {
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [availableServiceGroups, setAvailableServiceGroups] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [templateType, setTemplateType] = useState('new-request');
  const [variant, setVariant] = useState('INSIDE');
  const [selectedServices, setSelectedServices] = useState([]);
  const [dependencies, setDependencies] = useState([]);
  
  // Fetch available service groups and services on component mount
  useEffect(() => {
    const fetchServicesData = async () => {
      try {
        setInitialLoading(true);
        // Fetch all service groups
        const serviceGroupsResponse = await AppService.get('/api/service-groups');
        if (Array.isArray(serviceGroupsResponse)) {
          setAvailableServiceGroups(serviceGroupsResponse);
          console.log('Fetched service groups:', serviceGroupsResponse);
        } else {
          console.warn('Invalid service groups response:', serviceGroupsResponse);
          setAvailableServiceGroups([]);
        }
        
        // Fetch all services with fields
        const servicesWithFields = await workflowService.getAllServicesWithFields();
        if (Array.isArray(servicesWithFields)) {
          setAvailableServices(servicesWithFields);
          console.log('Fetched services with fields:', servicesWithFields);
        } else {
          console.warn('Invalid services response:', servicesWithFields);
          setAvailableServices([]);
        }
      } catch (error) {
        console.error('Error fetching services data:', error);
        setError('Failed to load service data. Please try again later.');
      } finally {
        setInitialLoading(false);
      }
    };
    
    fetchServicesData();
  }, []);
  
  // Handle basic workflow info changes
  const handleNameChange = (event) => {
    setName(event.target.value);
  };
  
  const handleDescriptionChange = (event) => {
    setDescription(event.target.value);
  };
  
  const handleTypeChange = (event) => {
    setTemplateType(event.target.value);
  };
  
  const handleVariantChange = (event) => {
    setVariant(event.target.value);
  };
  
  // Add a new service configuration
  const handleAddService = () => {
    setSelectedServices([
      ...selectedServices,
      {
        serviceGroupId: '',
        serviceGroupName: '',
        serviceId: '',
        serviceName: '',
        executionMode: 'SEQUENTIAL',
        required: true
      }
    ]);
  };
  
  // Update a service configuration
  const handleServiceConfigChange = (index, updatedConfig) => {
    const updated = [...selectedServices];
    updated[index] = updatedConfig;
    setSelectedServices(updated);
  };
  
  // Remove a service configuration
  const handleRemoveService = (index) => {
    const updated = selectedServices.filter((_, i) => i !== index);
    setSelectedServices(updated);
    
    // Also remove any dependencies for this service
    const updatedDeps = dependencies.filter(
      dep => dep.sourceId !== selectedServices[index].id
    );
    setDependencies(updatedDeps);
  };
  
  // Add a dependency between services
  const handleAddDependency = (sourceIndex, targetStepId, dependencyType) => {
    const sourceId = selectedServices[sourceIndex].id;
    const targetId = selectedServices[parseInt(targetStepId.split('-')[1]) - 1].id;
    
    setDependencies([
      ...dependencies,
      {
        sourceId,
        targetId,
        type: dependencyType
      }
    ]);
  };
  
  // Remove a dependency
  const handleRemoveDependency = (serviceIndex, dependencyIndex) => {
    const updatedDeps = [...dependencies];
    updatedDeps.splice(dependencyIndex, 1);
    setDependencies(updatedDeps);
  };
  
  // Find dependencies for a specific service
  const getDependenciesForService = (serviceIndex) => {
    return dependencies.filter(dep => 
      dep.sourceId === selectedServices[serviceIndex]?.id
    );
  };
  
  // Handle form submission and API call
  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!selectedServices || selectedServices.length === 0) {
      setError('Please select at least one service');
      return;
    }
    
    setLoading(true);
    setError(null);
    setSuccess(false);
    
    try {
      // Construct the payload
      const serviceConfigs = selectedServices.map(service => {
        // Find dependencies for this service
        const serviceDependencies = dependencies
          .filter(dep => dep.sourceId === service.id)
          .map(dep => dep.targetId);
        
        return {
          serviceId: service.id,
          executionMode: service.executionMode || 'SEQUENTIAL',
          required: service.required !== false, // Default to true unless explicitly set to false
          dependencies: serviceDependencies
        };
      });
      
      const payload = {
        name: name || `workflow${Math.floor(Math.random() * 100)}`,
        description: description || name || 'Generated workflow template',
        type: templateType || 'new-request',
        variant: variant || 'INSIDE',
        customerUid: customerUid,
        serviceConfigs: serviceConfigs
      };
      
      // Log the exact payload being sent
      console.log('\ud83d\udce3 API REQUEST PAYLOAD:', JSON.stringify(payload, null, 2));
      
      // Get auth token from prop or localStorage
      const authToken = token || localStorage.getItem('jwt_token');
      if (!authToken) {
        throw new Error('Authentication token not available');
      }
      
      console.log('\ud83d\udd11 Using auth token (first 20 chars):', authToken.substring(0, 20) + '...');
      
      // Call the API directly using the correct authenticated endpoint
      console.log('🔐 MAKING AUTHENTICATED API CALL to /api/workflow-templates/generate-multi-service');
      console.log('📊 PAYLOAD:', payload);
      console.log('🔑 AUTH TOKEN (first 20 chars):', authToken.substring(0, 20) + '...');
      
      const apiUrl = `${API_PATHS.WORKFLOW_TEMPLATES}/generate-multi-service`;
      console.log('🌐 CALLING URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(payload)
      });
      
      console.log('\ud83d\udcca API Response status:', response.status);
      
      // If the API returns an error, log it and throw an exception
      // Don't try to hide API errors with fallbacks
      if (!response.ok) {
        const errorText = await response.text();
        console.error(`\u274c API ERROR [${response.status}]:`, errorText);
        setError(`API Error: ${response.status} - ${errorText}`);
        setLoading(false);
        throw new Error(`API error: ${response.status} - ${errorText}`);
      }
      
      // Parse the raw response without any transformations
      const rawResponse = await response.json();
      
      // Log the entire raw response for debugging
      console.log('\ud83d\udcbe RAW SERVER RESPONSE:', JSON.stringify(rawResponse, null, 2));
      
      if (!rawResponse) {
        const errorMsg = 'Server returned empty response';
        console.error(`\u274c ${errorMsg}`);
        setError(errorMsg);
        throw new Error(errorMsg);
      }
      
      setSuccess(true);
      
      // Pass the raw server response directly to the callback
      // No transformations, exactly what the server sent
      if (onWorkflowGenerated) {
        onWorkflowGenerated(rawResponse);
      }
      
    } catch (error) {
      console.error('\u274c Error generating workflow template:', error);
      setError(`Error: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };
  
  if (initialLoading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Workflow template generated successfully
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Workflow Information
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Workflow Name"
              value={name}
              onChange={handleNameChange}
              fullWidth
              required
              error={name.trim() === ''}
              helperText={name.trim() === '' ? 'Workflow name is required' : ''}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={description}
              onChange={handleDescriptionChange}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Type</InputLabel>
              <Select
                value={templateType}
                onChange={handleTypeChange}
                label="Type"
              >
                <MenuItem value="new-request">New Request</MenuItem>
                <MenuItem value="follow-up">Follow Up</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Variant</InputLabel>
              <Select
                value={variant}
                onChange={handleVariantChange}
                label="Variant"
              >
                <MenuItem value="INSIDE">Inside</MenuItem>
                <MenuItem value="OUTSIDE">Outside</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              label="Customer UID"
              value={customerUid || ''}
              disabled={true}
              fullWidth
              helperText="Set via URL parameter or parent component"
            />
          </Grid>
        </Grid>
      </Paper>
      
      <Box my={3}>
        <Typography variant="h6" gutterBottom>
          Service Configurations
        </Typography>
        
        {selectedServices.length === 0 && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Click "Add Service" to configure workflow services
          </Alert>
        )}
        
        {selectedServices.map((serviceConfig, index) => (
          <ServiceConfigItem
            key={index}
            serviceIndex={index}
            serviceConfig={serviceConfig}
            availableServiceGroups={availableServiceGroups}
            availableServices={availableServices}
            onChange={handleServiceConfigChange}
            onRemove={handleRemoveService}
            availableServiceConfigs={selectedServices}
            onDependencyAdd={handleAddDependency}
            onDependencyRemove={handleRemoveDependency}
            dependencies={getDependenciesForService(index)}
          />
        ))}
        
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleAddService}
          sx={{ mt: 1 }}
        >
          Add Service
        </Button>
      </Box>
      
      <Box display="flex" justifyContent="flex-end" mt={3}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={loading || selectedServices.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Generate Workflow Template
        </Button>
      </Box>
    </Box>
  );
};

export default MultiServiceWorkflowBuilder;
